import { useEffect, type RefObject } from 'react';
import { useHero, heroThemeFor, applyHeroTheme, clearHeroTheme } from '@/entities/hero';

/**
 * HeroThemeApplier (ADR-0099 / ADR-0102) — a HEADLESS effect mounted in the kid shell. It resolves the chosen
 * hero's theme overrides ({@link heroThemeFor}) and applies them to the `.kid` SHELL ROOT (via `targetRef`)
 * while a kid is signed in, so the WHOLE kid experience recolours from the hero's own palette (choose princess
 * → a rose/gold/lavender world; robot → steel/teal). Cleared on no-hero / logout (unmount).
 *
 * It targets the `.kid` element, NOT `document.documentElement`: the kid stylesheet declares the scene
 * `--sky-*` tokens on `.kid`, and a class declaration on a closer ancestor would otherwise SHADOW an inline
 * override on `html` — so the hero's sky would never apply. Inline vars on the `.kid` root win over the `.kid`
 * stylesheet (same element) AND stay scoped to the kid subtree (never leak into the family/admin shell).
 */
export function HeroThemeApplier({
  targetRef,
}: {
  targetRef: RefObject<HTMLElement | null>;
}): null {
  // The single hero state (ADR-0102) — the theme follows the SAME hero the rest of the app renders.
  // `useHero` returns null when Play is off, so the palette clears with no extra gate here.
  const { hero } = useHero();
  const archetypeId = hero?.archetypeId ?? null;

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    const theme = heroThemeFor(archetypeId);
    if (theme) {
      applyHeroTheme(el, theme);
    } else {
      clearHeroTheme(el);
    }
    // Always clear on unmount (persona change / logout) so the hero palette never leaks into another shell.
    return () => clearHeroTheme(el);
  }, [archetypeId, targetRef]);

  return null;
}
